/**
 * Token Service - Cross-tab auth synchronization
 */

const AUTH_SYNC_KEY = 'auth_sync_timestamp';
const LOGOUT_EVENT_KEY = 'auth_logout_event';
const LOGOUT_EVENT_TIMEOUT = 2000;

export function getToken(): string | null {
  let token = localStorage.getItem('access');
  if (!token) token = sessionStorage.getItem('access');
  if (token) return JSON.parse(token) as string;
  return null;
}

export function setTokens(accessToken: string, _refreshToken?: string) {
  const access = JSON.stringify(accessToken);
  sessionStorage.setItem('access', access);
  localStorage.setItem('access', access);

  const timestamp = Date.now().toString();
  localStorage.setItem(AUTH_SYNC_KEY, timestamp);
  sessionStorage.setItem(AUTH_SYNC_KEY, timestamp);
  localStorage.removeItem(LOGOUT_EVENT_KEY);
}

export function setRefreshToken(token: string) {
  localStorage.setItem('refresh_token', JSON.stringify(token));
}

export function getRefreshToken(): string | null {
  const token = localStorage.getItem('refresh_token');
  if (token) return JSON.parse(token) as string;
  return null;
}

export function removeTokens() {
  localStorage.removeItem('access');
  sessionStorage.removeItem('access');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem(AUTH_SYNC_KEY);
  sessionStorage.removeItem(AUTH_SYNC_KEY);

  const logoutEvent = { timestamp: Date.now(), tabId: getTabId() };
  localStorage.setItem(LOGOUT_EVENT_KEY, JSON.stringify(logoutEvent));

  setTimeout(() => {
    const current = localStorage.getItem(LOGOUT_EVENT_KEY);
    if (current) {
      try {
        const event = JSON.parse(current);
        if (event.tabId === logoutEvent.tabId) localStorage.removeItem(LOGOUT_EVENT_KEY);
      } catch {
        localStorage.removeItem(LOGOUT_EVENT_KEY);
      }
    }
  }, LOGOUT_EVENT_TIMEOUT);
}

function getTabId(): string {
  if (!sessionStorage.getItem('tab_id')) {
    sessionStorage.setItem('tab_id', `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  }
  return sessionStorage.getItem('tab_id') || 'unknown';
}

export function checkCrossTabLogout(): boolean {
  const logoutEvent = localStorage.getItem(LOGOUT_EVENT_KEY);
  if (!logoutEvent) return false;
  try {
    const event = JSON.parse(logoutEvent);
    const currentTabId = sessionStorage.getItem('tab_id');
    if (event.tabId !== currentTabId) {
      if (Date.now() - event.timestamp < LOGOUT_EVENT_TIMEOUT) return true;
      localStorage.removeItem(LOGOUT_EVENT_KEY);
    }
  } catch {
    localStorage.removeItem(LOGOUT_EVENT_KEY);
  }
  return false;
}

export function clearStaleLogoutEvents(): void {
  const logoutEvent = localStorage.getItem(LOGOUT_EVENT_KEY);
  if (logoutEvent) {
    try {
      const event = JSON.parse(logoutEvent);
      if (Date.now() - event.timestamp > LOGOUT_EVENT_TIMEOUT) localStorage.removeItem(LOGOUT_EVENT_KEY);
    } catch {
      localStorage.removeItem(LOGOUT_EVENT_KEY);
    }
  }
}

export async function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const LOCK_KEY = 'refreshLock';
  const STALE_AFTER_MS = 10_000;

  const acquireLock = async (): Promise<void> => {
    while (true) {
      const existing = localStorage.getItem(LOCK_KEY);
      const now = Date.now();
      if (!existing || now - Number(existing) > STALE_AFTER_MS) {
        localStorage.setItem(LOCK_KEY, String(now));
        const confirm = localStorage.getItem(LOCK_KEY);
        if (confirm === String(now)) return;
      }
      await new Promise<void>((r) => setTimeout(r, 300));
    }
  };

  try {
    await acquireLock();
    return await fn();
  } finally {
    localStorage.removeItem(LOCK_KEY);
  }
}
